import React, { useContext, useCallback, Fragment, useState } from 'react';
import Store from '../Store/Store';
import './WorkTicket.css';
import Comment from '../Comment/Comment';
import EmployeeInfo from '../EmployeeInfo/EmployeeInfo';
import CbmContext from '../../Context/Cbm/cbmContext';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';
import { findStoreById } from '../Utils/helperFuncs';

const WORK_TYPE_OPTIONS = [
  { value: 'Annual_Strip', label: 'Annual Strip' },
  { value: 'Partial_Strip', label: 'Partial Strip' },
  { value: 'Full_Extraction', label: 'Full Extraction' },
  { value: 'Concrete_Restoration', label: 'Concrete Restoration' },
  { value: 'Store_Remodel', label: 'Store Remodel' },
  { value: 'Office_Recoat', label: 'Office Recoat' },
  { value: 'Deep_Scrub_And_Recoat', label: 'Deep Scrub And Recoat' },
  { value: 'Bounce_Back', label: 'Bounce Back' },
  { value: 'Fourth_Quarter_Prep', label: '4th Quarter Prep' },
  { value: 'Other', label: 'Other' },
];

const WorkTicket = ({ setPayLoad, setFormError, errMsg }) => {
  const cbmContext = useContext(CbmContext);
  const { stores, portalUser, submittedData, errorCode } = cbmContext;

  const [workTicketData, setWorkTicketData] = useState({
    employee: {
      employeeNumber: '',
      employeeFirst: '',
      employeeMiddle: '',
      employeeLast: '',
      employeeSecondLast: '',
    },
    notes: '',
    equipmentNeeded: '',
    projectLocation: null,
    typeOfWork: '',
    isBillable: '',
    supplyOrderDate: null,
    projectStartDate: null,
    projectEndDate: null,
    orderNumber: '',
    supplyOrderSubmitted: false,
    portalUser,
  });

  // Use unified form setup hook
  const { validationErrors, getFieldError } = useFormSetup({
    formName: 'workTicket',
    formData: workTicketData,
    setFormData: setWorkTicketData,
    setPayLoad,
    setFormError,
    submittedData,
    errorCode,
  });

  const changeEmpInfo = useCallback(
    (e) => {
      const { name, value } = e.target;
      setWorkTicketData((prev) => ({
        ...prev,
        employee: { ...prev.employee, [name]: value },
      }));
    },
    [setWorkTicketData]
  );

  const handleComments = useCallback(
    (e, commentType) => {
      const { value } = e.target;
      setWorkTicketData((prev) => ({ ...prev, [commentType]: value }));
    },
    [setWorkTicketData]
  );

  const changeProjectLocation = useCallback(
    (e) => {
      const { value } = e.target;
      const foundStore = findStoreById(stores, parseInt(value));
      setWorkTicketData((prev) => ({ ...prev, projectLocation: foundStore }));
    },
    [stores, setWorkTicketData]
  );
  return (
    <Fragment>
      <div className="heading">
        <h1>Work Ticket</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      <fieldset>
        <legend>Employee Info</legend>
        <EmployeeInfo
          payload={workTicketData}
          changeEmpInfo={changeEmpInfo}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
      <fieldset className="typeOfWork">
        <legend>Type of Work</legend>
        <div>
          <div
            className={
              errMsg && getFieldError('typeOfWork')
                ? 'workType error'
                : 'workType'
            }
          >
            <label htmlFor="workTypeId" className="required">
              Work type:{' '}
            </label>
            <select
              id="workTypeId"
              value={workTicketData.typeOfWork || ''}
              onChange={(e) =>
                setWorkTicketData({
                  ...workTicketData,
                  typeOfWork: e.target.value,
                })
              }
            >
              <option name="default" value="">
                Choose from list
              </option>
              {WORK_TYPE_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  name={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
            {errMsg && getFieldError('typeOfWork') && (
              <span className="error-text">{getFieldError('typeOfWork')}</span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('isBillable')
                ? 'billable error'
                : 'billable'
            }
          >
            <label htmlFor="billingTypeId" className="required">
              Billable/Non-Billable{' '}
            </label>
            <select
              id="billingTypeId"
              value={workTicketData.isBillable || ''}
              onChange={(e) =>
                setWorkTicketData({
                  ...workTicketData,
                  isBillable: e.target.value,
                })
              }
            >
              <option name="default" value="">
                Choose from list
              </option>
              <option name="billable" value="Billable">
                Billable
              </option>
              <option name="nonBill" value="Non-Billable">
                Non-Billable
              </option>
            </select>
            {errMsg && getFieldError('isBillable') && (
              <span className="error-text">{getFieldError('isBillable')}</span>
            )}
          </div>
        </div>
      </fieldset>
      <fieldset className="specialNotes">
        <legend>Notes</legend>
        <div className="comments">
          <Comment
            payload={workTicketData.notes}
            commentsId={'specialNotesId'}
            onHandleChange={(e) => handleComments(e, 'notes')}
            getFieldError={getFieldError}
            errMsg={errMsg}
          />
        </div>
      </fieldset>
      <fieldset className="equipmentNeeded">
        <legend>Equipment</legend>
        <div className="comments">
          <Comment
            payload={workTicketData.equipmentNeeded}
            commentsId={'equipmentNeededId'}
            onHandleChange={(e) => handleComments(e, 'equipmentNeeded')}
            getFieldError={getFieldError}
            errMsg={errMsg}
          />
        </div>
      </fieldset>
      <fieldset className="wtContainer">
        <legend>Store Info</legend>
        <Store
          storeId={'projectLocationId'}
          storeValue={workTicketData.projectLocation?.id}
          changeStoreInfo={changeProjectLocation}
          storeList={stores}
          header={'Project Location:'}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
      <fieldset className="supplyOrderStatus">
        <legend>Supply order status</legend>
        <div
          className={
            errMsg && getFieldError('supplyOrderSubmitted')
              ? 'supplyOrder error'
              : 'supplyOrder'
          }
        >
          <label className="required">Supply order submitted: </label>
          <div>
            Yes{' '}
            <input
              type="radio"
              checked={workTicketData.supplyOrderSubmitted === true}
              value="true"
              name="supplySubmitted"
              onChange={() =>
                setWorkTicketData({
                  ...workTicketData,
                  supplyOrderSubmitted: true,
                })
              }
            />{' '}
            No{' '}
            <input
              type="radio"
              checked={workTicketData.supplyOrderSubmitted === false}
              value="false"
              name="supplySubmitted"
              onChange={() =>
                setWorkTicketData({
                  ...workTicketData,
                  supplyOrderSubmitted: false,
                })
              }
            />
          </div>
          {errMsg && getFieldError('supplyOrderSubmitted') && (
            <span className="error-text">
              {getFieldError('supplyOrderSubmitted')}
            </span>
          )}
        </div>
        <div className="supplyOrderInfo">
          <div
            className={
              errMsg && getFieldError('supplyOrderDate')
                ? 'orderDate error'
                : 'orderDate'
            }
          >
            <label htmlFor="supplyOrderDateId">Supply order date:</label>
            <input
              type="date"
              id="supplyOrderDateId"
              value={workTicketData.supplyOrderDate || ''}
              onChange={(e) =>
                setWorkTicketData({
                  ...workTicketData,
                  supplyOrderDate: e.target.value,
                })
              }
            />
            {errMsg && getFieldError('supplyOrderDate') && (
              <span className="error-text">
                {getFieldError('supplyOrderDate')}
              </span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('orderNumber')
                ? 'orderNumber error'
                : 'orderNumber'
            }
          >
            <label htmlFor="supplyOrderNumId">Supply order number:</label>
            <input
              type="text"
              id="supplyOrderNumId"
              value={workTicketData.orderNumber || ''}
              onChange={(e) =>
                setWorkTicketData({
                  ...workTicketData,
                  orderNumber: e.target.value,
                })
              }
            />
            {errMsg && getFieldError('orderNumber') && (
              <span className="error-text">{getFieldError('orderNumber')}</span>
            )}
          </div>
        </div>
      </fieldset>
      <fieldset className="projectInfo">
        <legend>Project Info</legend>
        <div className="projectDates">
          <div
            className={
              errMsg && getFieldError('projectStartDate')
                ? 'projectStartDate error'
                : 'projectStartDate'
            }
          >
            <label htmlFor="projectStartDateId" className="required">
              Project start date:
            </label>
            <input
              type="date"
              id="projectStartDateId"
              value={workTicketData.projectStartDate || ''}
              onChange={(e) =>
                setWorkTicketData({
                  ...workTicketData,
                  projectStartDate: e.target.value,
                })
              }
            />
            {errMsg && getFieldError('projectStartDate') && (
              <span className="error-text">
                {getFieldError('projectStartDate')}
              </span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('projectEndDate')
                ? 'projectEndDate error'
                : 'projectEndDate'
            }
          >
            <label htmlFor="projectEndDateId" className="required">
              Project end date:
            </label>
            <input
              type="date"
              id="projectEndDateId"
              value={workTicketData.projectEndDate || ''}
              onChange={(e) =>
                setWorkTicketData({
                  ...workTicketData,
                  projectEndDate: e.target.value,
                })
              }
            />
            {errMsg && getFieldError('projectEndDate') && (
              <span className="error-text">
                {getFieldError('projectEndDate')}
              </span>
            )}
          </div>
        </div>
      </fieldset>
    </Fragment>
  );
};
// eslint-disable-next-line
WorkTicket.propTypes = {};

export default WorkTicket;
